import { Device } from '../helpers/Device'
import WebModel from '../models/WebModel'
import { MediaType } from '../enums/MediaType'
import mvFetch from '../helpers/mvFetch'

export namespace Flooring {
  let geo: IGeo | undefined

  export const requestGeolocation = async (model) => {
    if (!geo) {
      geo = await getGeo(model)
    }
    return geo
  }

  export const getDisplayFloor = (model: IWebViewModel | WebModel) => {
    return getFloor(model, MediaType.banner, geo)
  }

  export const getVideoFloor = (model: IWebViewModel | WebModel) => {
    return getFloor(model, MediaType.video, geo)
  }

  export const getOutstreamFloor = (model: IWebViewModel | WebModel) => {
    return getFloor(model, MediaType.outstream, geo)
  }
}

/** Ensure only results of this function are used by consuming agents */
type ComputedDevice = string & { flag: true }
function computeDevice(): ComputedDevice {
  if (Device.deviceType === 'desktop') {
    return Device.deviceType as ComputedDevice
  }

  const { deviceType, os } = Device
  const normalizedDevice = DeviceMap[deviceType]
  const normalizedOS = OSMap[os]

  return `${normalizedDevice} - ${normalizedOS}` as ComputedDevice
}

interface IGetIndexesArgs {
  device: ComputedDevice
  category: CategoryMap
  country: string
  mediaType: MediaType
}

function getIndexes({
  device,
  category,
  country,
  mediaType
}: IGetIndexesArgs): {
  device: number
  country: number
  category: number
  mediaType: number
} {
  const defaultValue = 1.00001
  const categoryDefaultValue = 1.08
  const indexes = {
    device: DeviceIndexes[device] || defaultValue,
    country: CountryIndexes[country] || CountryIndexes.default,
    category: CategoryIndexes[category] || categoryDefaultValue,
    mediaType: MediaTypeIndexes[mediaType]
  }

  return indexes
}

const baseIndex = 0.53
export function getFloor(
  model: IWebViewModel | WebModel,
  mediaType: MediaType,
  geo: IGeo | undefined
): number {
  const device = computeDevice()

  // NOTE: We're deliberately breaking the country-based multiplier here because
  // country-based floors currently work very poorly for the multipliers as they exist.
  // we need to fix this when new country-multipliers are created by Cynthia.
  // const country = (geo && geo.country_code) || 'other'
  const country = 'other'

  const category =
    CategoryMap[model.topLevelCategory && model.topLevelCategory.slug] ||
    CategoryMap.None
  const indexes = getIndexes({ device, country, category, mediaType })

  const floor = Object.keys(indexes).reduce(
    (acc, key) => acc * indexes[key],
    baseIndex
  )

  const roundedFloor = parseFloat(floor.toFixed(2))

  return roundedFloor
}

export const DeviceIndexes = {
  amp: 0.74,
  desktop: 1.46,
  other: 0.43,
  'phone - android': 1.54,
  'phone - ios': 0.58,
  'tablet - android': 1.53,
  'tablet - ios': 0.78
}

export const MediaTypeIndexes = {
  [MediaType.video]: 3.96,
  [MediaType.banner]: 0.42,
  [MediaType.outstream]: 2.97
}

export const CategoryIndexes = {
  None: 0.99,
  'Arts & Entertainment': 0.61,
  Education: 0.87,
  'Family & Parenting': 0.99,
  'Food & Drink': 0.97,
  'Health & Fitness': 0.99,
  'Hobbies & Interests': 0.89,
  'Home & Garden': 1.18,
  'Personal Finance': 1.48,
  Pets: 1.08,
  'Style & Fashion': 1.12,
  Travel: 0.89
}

export enum OSMap {
  iOS = 'ios',
  Android = 'android',
  desktop = 'desktop'
}

export enum DeviceMap {
  mobile = 'phone',
  tablet = 'tablet',
  desktop = 'desktop'
}

/** Maps slugs to display values being used by Cynthia */
export enum CategoryMap {
  None = 'None',
  'arts-and-entertainment' = 'Arts & Entertainment',
  'education' = 'Education',
  'family-and-parenting' = 'Family & Parenting',
  'food-and-drink' = 'Food & Drink',
  'health-and-fitness' = 'Health & Fitness',
  'hobbies-and-interests' = 'Hobbies & Interests',
  'home-and-garden' = 'Home & Garden',
  'personal-finance' = 'Personal Finance',
  'pets' = 'Pets',
  'style-and-fashion' = 'Style & Fashion',
  'travel' = 'Travel'
}

export interface IGeo {
  city: string
  country_code: string
}

const getGeo = async (model: WebModel | IWebViewModel): Promise<IGeo> => {
  const url = `https://scripts.mediavine.com/flooring/countryLookup` //`${model.launcherUrl}/flooring/countryLookup`
  let req
  try {
    req = await mvFetch.fetchJson(url)
  } catch (e) {
    return {
      country_code: 'US',
      city: 'unknown'
    }
  }

  return req.geo
}

export const CountryIndexes = {
  US: 2.22,
  UK: 1.07,
  CA: 1.47,
  AU: 1.41,
  IN: 0.34,
  DE: 1.08,
  ZA: 0.9,
  SG: 1.02,
  PH: 0.57,
  NZ: 1.3,
  NL: 0.9,
  MY: 0.78,
  IE: 0.9,
  FR: 0.9,
  MX: 0.9,
  ES: 0.9,
  AE: 1.03,
  NG: 0.9,
  SE: 1.17,
  BE: 0.9,
  ID: 0.68,
  CH: 1.47,
  AT: 1.05,
  IT: 0.9,
  BR: 0.9,
  HK: 1.27,
  NO: 1.07,
  JP: 0.9,
  AR: 0.49,
  PK: 0.35,
  default: 0.8
}
